import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Z_INDEX } from '../../../core/src/constants/zIndex';

interface StickyHeaderData {
  header: ReactNode;
  containerBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface StickyHeaderContextType {
  setStickyHeader: (data: StickyHeaderData | null) => void;
  stickyHeader: StickyHeaderData | null;
}

const StickyHeaderContext = createContext<StickyHeaderContextType | undefined>(undefined);

export const useStickyHeader = () => {
  const context = useContext(StickyHeaderContext);
  if (!context) {
    throw new Error('useStickyHeader must be used within a StickyHeaderProvider');
  }
  return context;
};

interface StickyHeaderProviderProps {
  children: ReactNode;
}

export const StickyHeaderProvider: React.FC<StickyHeaderProviderProps> = ({ children }) => {
  const [stickyHeader, setStickyHeader] = useState<StickyHeaderData | null>(null);

  return (
    <StickyHeaderContext.Provider value={{ setStickyHeader, stickyHeader }}>
      {children}
    </StickyHeaderContext.Provider>
  );
};

// Component that renders the sticky header at the top level
export const StickyHeaderRenderer: React.FC = () => {
  const { stickyHeader } = useStickyHeader();
  
  if (!stickyHeader) return null;
  
  const { containerBounds } = stickyHeader;
  
  return (
    <div
      style={{
        position: 'fixed',
        top: containerBounds.y,
        left: containerBounds.x,
        width: containerBounds.width,
        zIndex: Z_INDEX.sticky,
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
      className="sticky-header-renderer"
    >
      {stickyHeader.header}
    </div>
  );
};

// Helper component to make elements sticky using CSS
interface StickyHeaderProps {
  children: ReactNode;
  className?: string;
  top?: number;
  zIndex?: number;
}

export const StickyHeader: React.FC<StickyHeaderProps> = ({ 
  children, 
  className = '',
  top = 0,
  zIndex = Z_INDEX.sticky 
}) => {
  return (
    <div
      className={`sticky bg-white shadow-sm ${className}`}
      style={{
        position: 'sticky',
        top,
        zIndex,
      }}
    >
      {children}
    </div>
  );
};